import { DatePicker, Input, InputNumber, message, Modal, Select, Tooltip } from "antd"
import axiosInstance from "../../../../../axios/axiosInstance"
import { useEffect, useState } from "react"
import processError from "../../../../../utils/processError"
import dayjs from "dayjs"
import { InfoCircleOutlined, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons"
import {OrderRequest} from "../../../../../model/request";
import {OrderResponse} from "../../../../../model/response";

interface Props {
  isOpen: boolean
  onCloseModal: () => void
  onBeforeSuccess: () => void
  orderId: string
}

export default function OrderFormModal({ isOpen, onCloseModal, onBeforeSuccess, orderId }: Props) {
  const [orderData, setOrderData] = useState<OrderRequest | null>(null)
  const [originalOrder, setOriginalOrder] = useState<OrderResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const orderStateOptions = [
    { value: "PAID" as const, label: "Đã thanh toán" },
    { value: "UNPAID" as const, label: "Chưa thanh toán" },
  ]

  const paymentChannelOptions = [
    { value: "SEPAY" as const, label: "Sepay" },
    { value: "NAPAS" as const, label: "Napas" },
    { value: "VP_BANK" as const, label: "VP Bank" },
  ]

  const productTypeOptions = [
    { value: "TICKET" as const, label: "Vé" },
    { value: "BOOK" as const, label: "Sách" },
    { value: "PLAN" as const, label: "Gói" },
  ]

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price)
  }

  const onInputField = (e: any, fieldName: string) => {
    const orderTemp = { ...orderData } as any
    orderTemp[fieldName] = e.target.value
    setOrderData(orderTemp)
  }

  const onSelectField = (value: any, fieldName: string) => {
    const orderTemp = { ...orderData } as any
    orderTemp[fieldName] = value
    setOrderData(orderTemp)
  }

  const onNumberField = (value: any, fieldName: string) => {
    const orderTemp = { ...orderData } as any
    orderTemp[fieldName] = value || 0
    setOrderData(orderTemp)
  }

  const onPaymentField = (value: any, fieldName: string) => {
    const orderTemp = { ...orderData } as any
    if (!orderTemp.payment) {
      orderTemp.payment = {}
    }
    orderTemp.payment[fieldName] = value
    setOrderData(orderTemp)
  }

  const onPaymentDateField = (date: any) => {
    const orderTemp = { ...orderData } as any
    if (!orderTemp.payment) {
      orderTemp.payment = {}
    }
    orderTemp.payment.paymentDate = date ? date.toISOString() : null
    setOrderData(orderTemp)
  }

  const addOrderDetail = () => {
    if (!orderData) return
    const newOrderDetails = [...(orderData.orderDetails || [])]
    newOrderDetails.push({
      productQuantity: 1,
      pointQuantity: 0,
      productId: "",
      productName: "",
      productType: "PLAN",
      price: 0, // Add default price
    })
    setOrderData({
      ...orderData,
      orderDetails: newOrderDetails,
    })
  }

  const removeOrderDetail = (index: number) => {
    if (!orderData || !orderData.orderDetails) return
    const newOrderDetails = [...orderData.orderDetails]
    newOrderDetails.splice(index, 1)
    setOrderData({
      ...orderData,
      orderDetails: newOrderDetails,
    })
  }

  const updateOrderDetail = (index: number, field: string, value: any) => {
    if (!orderData || !orderData.orderDetails) return
    const newOrderDetails = [...orderData.orderDetails];
    (newOrderDetails[index] as any)[field] = value
    setOrderData({
      ...orderData,
      orderDetails: newOrderDetails,
    })
  }

  const loadOrderData = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance({
        url: `/order/${orderId}`,
        method: "get",
      })

      if (res.status === 200) {
        const orderResponse = res.data
        setOriginalOrder(orderResponse)

        // Convert response to request format
        const orderRequest: OrderRequest = {
          totalPrice: orderResponse.totalPrice || 0,
          discountPrice: orderResponse.discountPrice || 0,
          status: orderResponse.status,
          payment: {
            paymentChannel: orderResponse.payment?.paymentChannel,
            amount: orderResponse.payment?.amount || 0,
            userId: orderResponse.payment?.userId || orderResponse.userId,
            paymentDate: orderResponse.payment?.paymentDate,
          },
          orderDetails:
            orderResponse.orderDetails?.map((detail: any) => ({
              productQuantity: detail.productQuantity,
              pointQuantity: detail.pointQuantity,
              productId: detail.productId,
              productName: detail.productName,
              productType: detail.productType,
              price: detail.price, // Include price from response
            })) || [],
          firstName: orderResponse.firstName,
          lastName: orderResponse.lastName,
          customerName: orderResponse.customerName,
          companyName: orderResponse.companyName,
          taxCode: orderResponse.taxCode,
          country: orderResponse.country,
          address: orderResponse.address,
          postalCode: orderResponse.postalCode,
          province: orderResponse.province,
          email: orderResponse.email,
          phone: orderResponse.phone,
          extraInfo: orderResponse.extraInfo,
          discountCode: orderResponse.discountCode,
        }
        setOrderData(orderRequest)
      } else {
        message.error("Có lỗi xảy ra.")
      }
    } catch (err) {
      processError(err)
    }
    setLoading(false)
  }

  const onOk = async () => {
    try {
      if (orderData) {
        await axiosInstance({
          url: `/order/${orderId}`,
          method: "put",
          data: orderData,
        })
        onBeforeSuccess()
        message.success("Cập nhật thành công")
      }
    } catch (err) {
      processError(err)
    }
  }

  useEffect(() => {
    if (isOpen && orderId) {
      loadOrderData()
    }
  }, [isOpen, orderId])

  return (
    <Modal open={isOpen} onOk={onOk} onCancel={onCloseModal} title="Chỉnh sửa đơn hàng" width={900} loading={loading}>
      <div className="mt-8">
        {/* Order Basic Information */}
        {originalOrder && (
          <div className="mb-6 bg-gray-50 p-4 rounded">
            <h3 className="text-lg font-medium mb-4">Thông tin cơ bản</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="block text-gray-500">Mã đơn hàng:</span>
                <span className="font-medium">{originalOrder.orderCode}</span>
              </div>
              <div>
                <span className="block text-gray-500">Ngày tạo:</span>
                <span className="font-medium">{dayjs(originalOrder.createdAt).format("DD/MM/YYYY HH:mm")}</span>
              </div>
              <div>
                <span className="block text-gray-500">Trạng thái:</span>
                <Select
                  style={{ width: "100%" }}
                  value={orderData?.status}
                  onChange={(value) => onSelectField(value, "status")}
                  options={orderStateOptions}
                />
              </div>
            </div>
          </div>
        )}

        {/* Order Pricing */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Thông tin giá</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block mb-2">Tổng tiền (VND):</label>
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                value={orderData?.totalPrice}
                onChange={(value) => onNumberField(value, "totalPrice")}
                min={0}
                placeholder="Nhập tổng tiền..."
              />
            </div>
            <div>
              <label className="block mb-2">Giảm giá (VND):</label>
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                value={orderData?.discountPrice}
                onChange={(value) => onNumberField(value, "discountPrice")}
                min={0}
                placeholder="Nhập số tiền giảm giá..."
              />
            </div>
            <div>
              <span className="block text-gray-500 mb-2">Thành tiền:</span>
              <div className="p-2 bg-gray-100 rounded font-medium">
                {orderData ? formatPrice((orderData.totalPrice || 0) - (orderData.discountPrice || 0)) : "0 ₫"}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Thông tin khách hàng</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2">Họ:</label>
              <Input
                value={orderData?.firstName}
                onChange={(e) => onInputField(e, "firstName")}
                placeholder="Nhập họ..."
              />
            </div>
            <div>
              <label className="block mb-2">Tên:</label>
              <Input
                value={orderData?.lastName}
                onChange={(e) => onInputField(e, "lastName")}
                placeholder="Nhập tên..."
              />
            </div>
            <div>
              <label className="block mb-2">Tên khách hàng:</label>
              <Input
                value={orderData?.customerName}
                onChange={(e) => onInputField(e, "customerName")}
                placeholder="Nhập tên khách hàng..."
              />
            </div>
            <div>
              <label className="block mb-2">Email:</label>
              <Input value={orderData?.email} onChange={(e) => onInputField(e, "email")} placeholder="Nhập email..." />
            </div>
            <div>
              <label className="block mb-2">Số điện thoại:</label>
              <Input
                value={orderData?.phone}
                onChange={(e) => onInputField(e, "phone")}
                placeholder="Nhập số điện thoại..."
              />
            </div>
            <div>
              <label className="block mb-2">Công ty:</label>
              <Input
                value={orderData?.companyName}
                onChange={(e) => onInputField(e, "companyName")}
                placeholder="Nhập tên công ty..."
              />
            </div>
            <div>
              <label className="block mb-2">Mã số thuế:</label>
              <Input
                value={orderData?.taxCode}
                onChange={(e) => onInputField(e, "taxCode")}
                placeholder="Nhập mã số thuế..."
              />
            </div>
            <div>
              <label className="block mb-2">Quốc gia:</label>
              <Input
                value={orderData?.country}
                onChange={(e) => onInputField(e, "country")}
                placeholder="Nhập quốc gia..."
              />
            </div>
            <div className="col-span-2">
              <label className="block mb-2">Địa chỉ:</label>
              <Input
                value={orderData?.address}
                onChange={(e) => onInputField(e, "address")}
                placeholder="Nhập địa chỉ..."
              />
            </div>
            <div>
              <label className="block mb-2">Mã bưu điện:</label>
              <Input
                value={orderData?.postalCode}
                onChange={(e) => onInputField(e, "postalCode")}
                placeholder="Nhập mã bưu điện..."
              />
            </div>
            <div>
              <label className="block mb-2">Tỉnh/Thành phố:</label>
              <Input
                value={orderData?.province}
                onChange={(e) => onInputField(e, "province")}
                placeholder="Nhập tỉnh/thành phố..."
              />
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Thông tin thanh toán</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2">Kênh thanh toán:</label>
              <Select
                style={{ width: "100%" }}
                value={orderData?.payment?.paymentChannel}
                onChange={(value) => onPaymentField(value, "paymentChannel")}
                options={paymentChannelOptions}
                placeholder="Chọn kênh thanh toán..."
              />
            </div>
            <div>
              <label className="block mb-2">Số tiền:</label>
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                value={orderData?.payment?.amount}
                onChange={(value) => onPaymentField(value, "amount")}
                min={0}
                placeholder="Nhập số tiền..."
              />
            </div>
            <div>
              <label className="block mb-2">User ID:</label>
              <Input
                value={orderData?.payment?.userId}
                onChange={(e) => onPaymentField(e.target.value, "userId")}
                placeholder="Nhập User ID..."
              />
            </div>
            <div>
              <label className="block mb-2">Ngày thanh toán:</label>
              <DatePicker
                style={{ width: "100%" }}
                showTime
                value={orderData?.payment?.paymentDate ? dayjs(orderData.payment.paymentDate) : null}
                onChange={onPaymentDateField}
                format="DD/MM/YYYY HH:mm"
              />
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Chi tiết đơn hàng</h3>
            <div className="flex items-center">
              <button
                type="button"
                onClick={addOrderDetail}
                className="flex items-center text-blue-500 hover:text-blue-700"
              >
                <PlusOutlined className="mr-1" /> Thêm sản phẩm
              </button>
            </div>
          </div>

          {orderData?.orderDetails?.map((detail, index) => (
            <div key={index} className="border rounded p-4 mb-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block mb-2">Tên sản phẩm:</label>
                  <Input
                    value={detail.productName}
                    onChange={(e) => updateOrderDetail(index, "productName", e.target.value)}
                    placeholder="Nhập tên sản phẩm..."
                  />
                </div>
                <div>
                  <label className="block mb-2">ID sản phẩm:</label>
                  <Input
                    value={detail.productId}
                    onChange={(e) => updateOrderDetail(index, "productId", e.target.value)}
                    placeholder="Nhập ID sản phẩm..."
                  />
                </div>
                <div>
                  <label className="block mb-2">Loại sản phẩm:</label>
                  <Select
                    style={{ width: "100%" }}
                    value={detail.productType}
                    onChange={(value) => updateOrderDetail(index, "productType", value)}
                    options={productTypeOptions}
                    placeholder="Chọn loại sản phẩm..."
                  />
                </div>
                <div>
                  <label className="block mb-2">Số lượng sản phẩm:</label>
                  <InputNumber
                    style={{ width: "100%" }}
                    value={detail.productQuantity}
                    onChange={(value) => updateOrderDetail(index, "productQuantity", value || 1)}
                    min={1}
                    placeholder="Nhập số lượng..."
                  />
                </div>
                <div>
                  <label className="block mb-2">Giá (VND):</label>
                  <InputNumber
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ""))}
                    value={detail.price}
                    onChange={(value) => updateOrderDetail(index, "price", value || 0)}
                    min={0}
                    placeholder="Nhập giá..."
                  />
                </div>
                <div>
                  <label className="block mb-2">Số điểm:</label>
                  <InputNumber
                    style={{ width: "100%" }}
                    value={detail.pointQuantity}
                    onChange={(value) => updateOrderDetail(index, "pointQuantity", value || 0)}
                    min={0}
                    placeholder="Nhập số điểm..."
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeOrderDetail(index)}
                    className="p-2 text-red-500 hover:text-red-700"
                  >
                    <MinusCircleOutlined /> Xóa
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-4">Thông tin bổ sung</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block mb-2">Mã giảm giá:</label>
              <Input
                value={orderData?.discountCode}
                onChange={(e) => onInputField(e, "discountCode")}
                placeholder="Nhập mã giảm giá..."
              />
            </div>
            <div>
              <label className="block mb-2">Thông tin thêm:</label>
              <Input.TextArea
                rows={3}
                value={orderData?.extraInfo}
                onChange={(e) => onInputField(e, "extraInfo")}
                placeholder="Nhập thông tin thêm..."
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

